import { useState, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Users, Database, Calendar, X, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { UpgradePlansModal } from "@/components/subscription/upgrade-plans-modal";

interface LimitAlert {
  type: 'user_limit' | 'license_limit' | 'subscription_expiry';
  severity: 'warning' | 'critical';
  message: string;
  currentUsage?: number;
  limit?: number;
  daysUntilExpiry?: number;
  actionRequired: boolean;
}

export function LimitAlertsBanner() {
  const [dismissedAlerts, setDismissedAlerts] = useState<string[]>([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Check plan limits
  const { data: planLimitData } = useQuery({
    queryKey: ['/api/subscription/plan-limits'],
    refetchInterval: 30000, // Check every 30 seconds
  });

  // Check subscription status
  const { data: subscriptionData } = useQuery({
    queryKey: ['/api/subscription-status'],
    refetchInterval: 60000, // Check every minute
  });

  const alerts: LimitAlert[] = [];

  // Plan limit alerts
  if (planLimitData && !planLimitData.isWithinLimits) {
    if (planLimitData.violations.some((v: string) => v.includes('user'))) {
      alerts.push({
        type: 'user_limit',
        severity: 'critical',
        message: `User limit exceeded: ${planLimitData.currentUsage.users}/${planLimitData.planLimits.users} users`,
        currentUsage: planLimitData.currentUsage.users,
        limit: planLimitData.planLimits.users,
        actionRequired: true
      });
    }
    
    if (planLimitData.violations.some((v: string) => v.includes('license'))) {
      alerts.push({
        type: 'license_limit',
        severity: 'critical',
        message: `License limit exceeded: ${planLimitData.currentUsage.licenses}/${planLimitData.planLimits.licenses} licenses`,
        currentUsage: planLimitData.currentUsage.licenses,
        limit: planLimitData.planLimits.licenses,
        actionRequired: true
      });
    }
  }

  // Subscription expiry alerts
  if (subscriptionData?.plan?.nextBillingDate) {
    const nextBilling = new Date(subscriptionData.plan.nextBillingDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((nextBilling.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry <= 3 && daysUntilExpiry > 0) {
      alerts.push({
        type: 'subscription_expiry',
        severity: 'warning',
        message: `Subscription expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}`,
        daysUntilExpiry,
        actionRequired: true
      });
    } else if (daysUntilExpiry <= 0) {
      alerts.push({
        type: 'subscription_expiry',
        severity: 'critical',
        message: 'Subscription has expired',
        daysUntilExpiry,
        actionRequired: true
      });
    }
  }

  // Filter out dismissed alerts
  const activeAlerts = alerts.filter(alert => 
    !dismissedAlerts.includes(`${alert.type}-${alert.severity}`)
  );

  const dismissAlert = (alertKey: string) => {
    setDismissedAlerts(prev => [...prev, alertKey]);
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'user_limit': return <Users className="w-4 h-4" />;
      case 'license_limit': return <Database className="w-4 h-4" />;
      case 'subscription_expiry': return <Calendar className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getAlertColor = (severity: string) => {
    return severity === 'critical' 
      ? 'border-red-200 bg-red-50 text-red-800' 
      : 'border-orange-200 bg-orange-50 text-orange-800';
  };

  if (activeAlerts.length === 0) {
    return null;
  }

  return (
    <>
      <div className="space-y-2 mb-6">
        {activeAlerts.map((alert, index) => {
          const alertKey = `${alert.type}-${alert.severity}`;
          
          return (
            <Alert key={alertKey} className={`${getAlertColor(alert.severity)} border-l-4`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`p-1 rounded ${alert.severity === 'critical' ? 'bg-red-100' : 'bg-orange-100'}`}>
                    {getAlertIcon(alert.type)}
                  </div>
                  
                  <div className="flex-1">
                    <AlertDescription className="font-medium">
                      {alert.message}
                    </AlertDescription>
                    
                    {alert.actionRequired && (
                      <div className="flex items-center space-x-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {alert.severity === 'critical' ? 'Action Required' : 'Warning'}
                        </Badge>
                        
                        {(alert.type === 'user_limit' || alert.type === 'license_limit') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowUpgradeModal(true)}
                            className="h-6 px-2 text-xs"
                          >
                            Upgrade Plan
                            <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                        
                        {alert.type === 'subscription_expiry' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.location.href = '/subscription'}
                            className="h-6 px-2 text-xs"
                          >
                            Renew Now
                            <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => dismissAlert(alertKey)}
                  className="h-6 w-6 p-0 hover:bg-white/50"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </Alert>
          );
        })}
      </div>

      <UpgradePlansModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentPlan={subscriptionData?.plan?.name || 'Free'}
        currentUsage={{
          users: planLimitData?.currentUsage?.users || 0,
          licenses: planLimitData?.currentUsage?.licenses || 0,
          applications: planLimitData?.currentUsage?.applications || 0
        }}
      />
    </>
  );
}